
<template>
    <v-dialog
        :value="true"
        persistent
        max-width="460"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        scrollable
        transition="dialog-bottom-transition"
    >
        <v-card class="ma-card" :style="cardStyle">
            <!-- Header (tema do tenant) -->
            <div class="ma-header" :style="{ background: primaryColor }">
                <v-btn icon dark small class="ma-close" @click="$emit('close')">
                    <v-icon size="22">mdi-close</v-icon>
                </v-btn>

                <v-avatar size="64" class="ma-avatar">
                    <span class="ma-avatar-initials" :style="{ color: primaryColor }">{{ initials }}</span>
                </v-avatar>

                <div class="ma-name">{{ cliente.nome || $t('conta.usuario') }}</div>
                <div v-if="cliente.whatsapp" class="ma-phone">{{ cliente.whatsapp }}</div>

                <div v-if="cliente.pontos" class="ma-points">
                    <v-icon x-small color="white" class="mr-1">mdi-star</v-icon>
                    {{ $t('conta.pontosFidelidadeN', { n: cliente.pontos }) }}
                </div>
            </div>

            <!-- Tabs -->
            <v-tabs
                v-model="tab"
                grow
                :color="primaryColor"
                :slider-color="primaryColor"
                height="46"
            >
                <v-tab class="text-none font-weight-medium">
                    <v-icon small class="mr-2">mdi-account-outline</v-icon>
                    {{ $t('conta.meusDados') }}
                </v-tab>
                <v-tab class="text-none font-weight-medium">
                    <v-icon small class="mr-2">mdi-map-marker-outline</v-icon>
                    {{ $t('conta.enderecos') }}
                </v-tab>
                <v-tab class="text-none font-weight-medium">
                    <v-icon small class="mr-2">mdi-receipt-text-outline</v-icon>
                    {{ $t('conta.pedidos') }}
                </v-tab>
            </v-tabs>

            <v-divider />

            <!-- Conteúdo -->
            <v-card-text class="ma-content pa-0">
                <v-tabs-items v-model="tab">
                    <!-- Meus dados -->
                    <v-tab-item>
                        <div class="pa-4">
                            <div
                                v-for="(row, i) in dataRows"
                                :key="i"
                                class="ma-data-item"
                            >
                                <v-avatar size="38" class="ma-data-icon" :style="{ background: tintColor }">
                                    <v-icon size="20" :color="primaryColor">{{ row.icon }}</v-icon>
                                </v-avatar>
                                <div class="ma-data-text">
                                    <div class="ma-data-label">{{ row.label }}</div>
                                    <div class="ma-data-value">{{ row.value }}</div>
                                </div>
                            </div>
                        </div>
                    </v-tab-item>

                    <!-- Endereços -->
                    <v-tab-item>
                        <EnderecosManager />
                    </v-tab-item>

                    <!-- Pedidos -->
                    <v-tab-item>
                        <MyOrders />
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import MyOrders from './MyOrders';
import EnderecosManager from './EnderecosManager';
import { mapState } from 'vuex';

export default {
    name: 'MyAccount',

    components: { MyOrders, EnderecosManager },

    props: {
        initialTab: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            tab: this.initialTab,
        };
    },

    computed: {
        ...mapState([
            'cliente',
        ]),

        primaryColor() {
            return (this.$theme && this.$theme.primary)
                || (this.$vuetify.theme.themes.light && this.$vuetify.theme.themes.light.primary)
                || '#d32f2f';
        },

        // Fundo suave (tinta clara) p/ os ícones, derivado da cor primária.
        tintColor() {
            return this.hexToRgba(this.primaryColor, 0.12);
        },

        cardStyle() {
            return this.$vuetify.breakpoint.xsOnly ? {} : { borderRadius: '16px' };
        },

        initials() {
            const nome = this.cliente && this.cliente.nome ? this.cliente.nome : '';
            const parts = nome.trim().split(/\s+/).filter(Boolean);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
            }
            return (parts[0] ? parts[0][0] : '?').toUpperCase();
        },

        dataRows() {
            const c = this.cliente || {};
            return [
                { icon: 'mdi-account-outline', label: this.$t('conta.nome'), value: c.nome || '—' },
                { icon: 'mdi-whatsapp', label: this.$t('conta.whatsappRotulo'), value: c.whatsapp || '—' },
                { icon: 'mdi-star-outline', label: this.$t('conta.pontosFidelidade'), value: this.$t('conta.nPontos', { n: c.pontos || 0 }) },
            ];
        },
    },

    methods: {
        hexToRgba(hex, alpha) {
            const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
            if (!m) {
                return `rgba(0, 0, 0, ${alpha})`;
            }
            const r = parseInt(m[1], 16);
            const g = parseInt(m[2], 16);
            const b = parseInt(m[3], 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        },
    },
}
</script>

<style scoped>
.ma-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* ===== Header ===== */
.ma-header {
    position: relative;
    padding: 26px 16px 22px;
    text-align: center;
    color: #fff;
}

.ma-close {
    position: absolute;
    top: 8px;
    right: 8px;
}

.ma-avatar {
    background: #fff !important;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.18);
}

.ma-avatar-initials {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: 0.5px;
}

.ma-name {
    margin-top: 12px;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.2;
}

.ma-phone {
    font-size: 0.85rem;
    opacity: 0.9;
    margin-top: 2px;
}

.ma-points {
    display: inline-flex;
    align-items: center;
    margin-top: 14px;
    font-size: 0.76rem;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.22);
    padding: 5px 14px;
    border-radius: 20px;
    backdrop-filter: blur(2px);
}

/* ===== Conteúdo ===== */
.ma-content {
    flex: 1 1 auto;
    overflow-y: auto;
}

.ma-data-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 2px;
}

.ma-data-item + .ma-data-item {
    border-top: 1px solid #f2f2f2;
}

.ma-data-icon {
    flex-shrink: 0;
}

.ma-data-text {
    min-width: 0;
}

.ma-data-label {
    font-size: 0.72rem;
    color: #9e9e9e;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 600;
}

.ma-data-value {
    font-size: 0.96rem;
    color: #333;
    font-weight: 600;
    margin-top: 2px;
    word-break: break-word;
}
</style>
